import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import styled, { keyframes, css } from 'styled-components';
import { showLoadingScreen } from 'a/hub/actions/metaGameActions';
import { reportNavigation } from 'a/hub/actions/metagameSearchlight';
import GameIds from 'a/hub/data/GameIds';
import { REFRESH_TIMEOUT } from 'a/hub/data/constants';
import Env from 'lib/Env';
import {
  DESTINATION_GROUP,
  NAVIGATION_ORIGIN,
  NAVIGATION_TRIGGER_TYPE,
} from 'root/lib/SearchlightDispatcher';
import {
  getIsChallengeQuest,
  getCurrentTFEEpisodeConfig,
  getCurrentQuestId,
  getCurrentTFEEpisodePath,
  getTFEPartText,
  getIsWellBelowBenchmark,
} from 'root/activities/hub/selectors/workshopSelectors';
import { autoCompleteTFESession, setWorkshopStepAsInProgress } from 'root/activities/hub/actions/workshopActions';
import MissionIcon from '../../../../../components/svgComponents/GameIcon';
import useWorkshopSfx from '../../../hooks/useWorkshopSfx';
import FadeAnimation from '../../portal/FadeAnimation';
import { ModalContainer, AutoCompleteButton } from '../sharedComponents';
import BurstM from '../svgs/Burst/BurstM.svg';
import ModalLargeBackground from '../svgs/Common/WideBackground.svg';
import {
  Background,
  Burst,
  GoButton,
  CloseModalButton,
  Decoline,
  HeaderContainer,
  HeaderTitle,
  MissionIconContainer,
  TFEDescription,
  TFEName,
  TFESubDescription,
  DescriptionContainer,
  IconContainer,
  ContentContainer,
} from './styledComponents';
import DecolineShort from './svgs/DecolineShort.svg';

const TFELaunchModal = ({ closeModal }) => {
  const [showPortalFadeAnimation, setShowPortalFadeAnimation] = useState(false);

  const questId = useSelector(getCurrentQuestId);
  const isChallengeQuest = useSelector(getIsChallengeQuest);

  const episodeConfig = useSelector(getCurrentTFEEpisodeConfig);
  const episodePath = useSelector(getCurrentTFEEpisodePath);
  const episodePosfix = useSelector(getTFEPartText);

  const isWellBelowBenchmark = useSelector(getIsWellBelowBenchmark);

  let episodeName = episodeConfig ? episodeConfig.name : 'No mission available';
  if (isWellBelowBenchmark && episodeConfig && episodePosfix) {
    episodeName += ` (${episodePosfix})`;
  }
  const dispatch = useDispatch();

  const { playTap, playSfxFromMap, audioMap } = useWorkshopSfx();

  useEffect(() => {
    playSfxFromMap(audioMap.close_reading_launch);
  }, []);

  const onCloseButtonPress = () => {
    playTap(true);
    closeModal();
  };

  const onGoButtonPress = async () => {
    playTap();
    setShowPortalFadeAnimation(true);
    closeModal();

    if (!isChallengeQuest) {
      if (episodeConfig !== undefined) {
        dispatch(
          reportNavigation({
            destination: GameIds.CLOSE_READING,
            destinationGroup: DESTINATION_GROUP.CLOSE_READING,
            origin: NAVIGATION_ORIGIN.WORKSHOP,
            trigger: 'TFELaunchPopup',
            triggerType: NAVIGATION_TRIGGER_TYPE.MANUAL,
          }),
        );
        dispatch({
          gameId: GameIds.METAGAME,
          type: 'SEARCHLIGHT_DISPATCH_EVENT',
          payload: {
            gameId: GameIds.METAGAME,
            application: {
              gameId: GameIds.CLOSE_READING,
              eventType: 'APP_LAUNCHED',
              details: {
                questId,
                isQuestWorkflow: true,
              },
            },
          },
        });
        dispatch(showLoadingScreen());
        await dispatch(setWorkshopStepAsInProgress(true));

        // start redirect to Close Reading(TFE) after 3 seconds
        setTimeout(() => {
          const TFE_REDIRECT_URL = `${Env.CLOSE_READING_URL}${episodePath}`;
          window.location.href = TFE_REDIRECT_URL;
        }, REFRESH_TIMEOUT);
      }
    }
  };

  return showPortalFadeAnimation ? (
    <FadeAnimation onEnd={closeModal} />
  ) : (
    <ModalContainer
      role="dialog"
      aria-modal="true"
      aria-labelledby="tfe-modal-title"
      data-testid="tfe-launch-modal"
      data-dd-action-name="tfe-launch-modal"
    >
      <CloseModalButton
        onClick={onCloseButtonPress}
        data-testid="tfe-launch-modal-close-button"
        data-dd-action-name="tfe-launch-modal:close"
      />
      <Background src={ModalLargeBackground} alt="" />
      <HeaderContainer>
        <HeaderTitle
          id="tfe-modal-title"
          data-testid="tfe-launch-modal-title"
        >
          MISSION {parseInt(episodeConfig.id.slice(-2), 10)}
        </HeaderTitle>
      </HeaderContainer>
      <ContentContainer>
        <IconContainer>
          <Burst src={BurstM} alt="" />
          <MissionIconContainer data-testid="tfe-launch-modal-mission-image">
            <MissionIcon gameId={`close_reading_${episodeConfig.id}`} width={136} height={136} />
          </MissionIconContainer>
        </IconContainer>
        <DescriptionContainer>
          <StyledTFEName
            delay="0.1s"
            style={{ fontSize: episodeName.length > 25 ? '26' : '27' }}
            data-testid="tfe-launch-modal-episode-name"
          >
            {episodeName}
          </StyledTFEName>
          <StyledTFESubDescription delay="0.1s" data-testid="tfe-launch-modal-episode-description">
            {episodeConfig
              ? `${episodeConfig.moduleName}: ${episodeConfig.teacherName}`
              : 'No mission available'}
          </StyledTFESubDescription>
          <StyledDecoline src={DecolineShort} alt="" delay="0.1s" />
          <StyledTFEDescription
            delay="0.1s"
            data-testid="tfe-launch-modal-mission-explanation"
          >
            {episodeConfig ? episodeConfig.description : ''}
          </StyledTFEDescription>
        </DescriptionContainer>
      </ContentContainer>
      <GoButton onClick={onGoButtonPress} ariaLabel="Launch Mission" actionName="tfe-launch-modal:go" />
      <AutoCompleteButton
        onClick={() => {
          setShowPortalFadeAnimation(true);
          closeModal();
          dispatch(autoCompleteTFESession());
        }}
      />
    </ModalContainer>
  );
};

TFELaunchModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
};

export default TFELaunchModal;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const fadeInStyle = (delay = '0.5s') => css`
  opacity: 0;
  animation: ${fadeIn} 2s forwards;
  animation-delay: ${delay || 0};
`;

const StyledTFEName = styled(TFEName)`
  ${fadeInStyle(props => props.delay)}
`;

const StyledTFESubDescription = styled(TFESubDescription)`
  ${fadeInStyle(props => props.delay)}
`;

const StyledTFEDescription = styled(TFEDescription)`
  ${fadeInStyle(props => props.delay)}
`;

const StyledDecoline = styled(Decoline)`
  ${fadeInStyle(props => props.delay)}
`;
